import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import api, { getApiErrorMessage } from '../../services/api';

const WB_CARDS = [
  {
    id: 'taken_not_accepted',
    title: 'Забраны, но не приняты',
    description: 'completedDt есть, статус WB «Выдано», у нас статус «Ожидает получения WB».',
  },
  {
    id: 'taken_accepted',
    title: 'Забраны и приняты',
    description: 'completedDt есть, статус WB «Выдано», у нас статус уже не начальный.',
  },
  {
    id: 'in_transit_accepted',
    title: 'В пути, но приняты',
    description: 'Статус WB «В пути в пвз», у нас статус уже не начальный.',
  },
];

const FOREIGN_CARDS = [
  {
    id: 'processed',
    title: 'Обработанные возвраты',
    description: 'Возвраты в одном из конечных статусов.',
  },
  {
    id: 'unprocessed',
    title: 'НЕ обработанные возвраты',
    description: 'Возвраты в начальном статусе, кроме «не найденных».',
  },
  {
    id: 'intermediate',
    title: 'Заказы с промежуточным статусом',
    description: 'Статус между начальным и конечными.',
  },
  {
    id: 'not_found',
    title: 'Не найденные возвраты',
    description: 'Для seller_id заказа не найден магазин.',
  },
];

function downloadBlob(res, filename) {
  const blob = res?.data;
  if (!blob) throw new Error('Пустой файл выгрузки.');
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  a.remove();
}

function formatDateTime(value) {
  if (!value) return '-';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return String(value);
  return dt.toLocaleString();
}

function MetricCard({ card, value, loading, exporting, onDownload }) {
  return (
    <Col md={6} xl={4} className="mb-3">
      <Card className="border-0 shadow-sm h-100 admin-analytics-card">
        <Card.Body className="d-flex flex-column">
          <div className="d-flex justify-content-between gap-3 align-items-start mb-2">
            <div>
              <div className="fw-semibold">{card.title}</div>
              <div className="small text-muted mt-1">{card.description}</div>
            </div>
            <Badge bg="light" text="dark" className="fs-6 border">
              {loading ? <Spinner animation="border" size="sm" /> : value ?? 0}
            </Badge>
          </div>
          <div className="mt-auto pt-2">
            <Button
              variant="outline-success"
              size="sm"
              disabled={loading || exporting}
              onClick={() => onDownload(card.id)}
            >
              {exporting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Экспорт...
                </>
              ) : (
                'Download Excel'
              )}
            </Button>
          </div>
        </Card.Body>
      </Card>
    </Col>
  );
}

function LogisticsStatusGroup({ group, expanded, exporting, onToggle, onDownload }) {
  const orders = group.orders || [];
  return (
    <Card className="border-0 shadow-sm mb-3 admin-analytics-card">
      <Card.Body>
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <div>
            <div className="fw-semibold">{group.status_label}</div>
            <div className="small text-muted">{group.status}</div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Badge bg={group.is_initial ? 'secondary' : 'primary'} className="fs-6">
              {group.count}
            </Badge>
            <Button
              variant="outline-success"
              size="sm"
              disabled={exporting}
              onClick={() => onDownload(group.status)}
            >
              {exporting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Экспорт...
                </>
              ) : (
                'Download Excel'
              )}
            </Button>
            <Button variant="outline-primary" size="sm" onClick={onToggle}>
              {expanded ? 'Скрыть возвраты' : 'Раскрыть возвраты'}
            </Button>
          </div>
        </div>

        {expanded ? (
          <div className="table-responsive mt-3">
            <Table size="sm" hover className="align-middle mb-0 admin-logistics-orders-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Номер возврата</th>
                  <th>readyToReturnDt</th>
                  <th>Магазин</th>
                  <th>seller_id</th>
                  <th>Товар</th>
                  <th>ШК / barcode</th>
                  <th>ПВЗ выдачи</th>
                  <th>WB статус</th>
                  <th>Обновлен</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id}>
                    <td>{order.id}</td>
                    <td>{order.platform_order_no || '-'}</td>
                    <td>{formatDateTime(order.ready_to_return_dt)}</td>
                    <td>{order.store_name || '-'}</td>
                    <td>{order.seller_id || '-'}</td>
                    <td className="text-wrap-col">
                      {order.sku_name || '-'}
                      {order.item_count > 1 ? (
                        <span className="text-muted"> +{order.item_count - 1}</span>
                      ) : null}
                    </td>
                    <td>
                      <div>{order.shk_id || '-'}</div>
                      <div className="small text-muted">{order.barcode || order.sku_barcode || '-'}</div>
                    </td>
                    <td className="text-wrap-col">{order.dst_office_address || '-'}</td>
                    <td>
                      {order.wb_status || '-'}
                      {order.is_status_active !== null && order.is_status_active !== undefined ? (
                        <div className="small text-muted">active: {order.is_status_active}</div>
                      ) : null}
                    </td>
                    <td>{formatDateTime(order.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : null}
      </Card.Body>
    </Card>
  );
}

function AdminConsolePage() {
  const [activeSection, setActiveSection] = useState('wb');

  const [wbFilters, setWbFilters] = useState({
    return_scope: '',
    completed_from: '',
    completed_to: '',
    our_office: '',
    store_q: '',
  });
  const [wbData, setWbData] = useState({ cards: {}, summary: {} });
  const [wbLoading, setWbLoading] = useState(false);
  const [wbExporting, setWbExporting] = useState('');

  const [foreignFilters, setForeignFilters] = useState({ date_from: '', date_to: '' });
  const [foreignData, setForeignData] = useState({ cards: {} });
  const [foreignLoading, setForeignLoading] = useState(false);
  const [foreignExporting, setForeignExporting] = useState('');

  const [logisticsFilters, setLogisticsFilters] = useState({ date_from: '', date_to: '' });
  const [logisticsData, setLogisticsData] = useState({ summary: {}, status_groups: [] });
  const [logisticsLoading, setLogisticsLoading] = useState(false);
  const [logisticsLoaded, setLogisticsLoaded] = useState(false);
  const [logisticsExporting, setLogisticsExporting] = useState('');
  const [expandedLogisticsStatuses, setExpandedLogisticsStatuses] = useState({});

  const [error, setError] = useState('');

  const loadWb = useCallback(async () => {
    setWbLoading(true);
    setError('');
    try {
      const data = await api.getAdminAnalyticsWBApi(wbFilters);
      setWbData(data || { cards: {}, summary: {} });
    } catch (err) {
      setError(getApiErrorMessage(err, 'Не удалось загрузить аналитику WB API.'));
    } finally {
      setWbLoading(false);
    }
  }, [wbFilters]);

  const loadForeign = useCallback(async () => {
    setForeignLoading(true);
    setError('');
    try {
      const data = await api.getAdminAnalyticsForeign(foreignFilters);
      setForeignData(data || { cards: {} });
    } catch (err) {
      setError(getApiErrorMessage(err, 'Не удалось загрузить аналитику зарубежных заказов.'));
    } finally {
      setForeignLoading(false);
    }
  }, [foreignFilters]);

  const loadLogistics = useCallback(async () => {
    setLogisticsLoading(true);
    setError('');
    try {
      const data = await api.getAdminAnalyticsLogistics(logisticsFilters);
      setLogisticsData(data || { summary: {}, status_groups: [] });
      setLogisticsLoaded(true);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Не удалось загрузить аналитику логистики.'));
    } finally {
      setLogisticsLoading(false);
    }
  }, [logisticsFilters]);

  useEffect(() => {
    loadWb();
    loadForeign();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial dashboard load
  }, []);

  useEffect(() => {
    if (activeSection === 'logistics' && !logisticsLoaded) {
      loadLogistics();
    }
  }, [activeSection, loadLogistics, logisticsLoaded]);

  const updateWbFilter = (name, value) => {
    setWbFilters((prev) => ({ ...prev, [name]: value }));
  };

  const updateForeignFilter = (name, value) => {
    setForeignFilters((prev) => ({ ...prev, [name]: value }));
  };

  const updateLogisticsFilter = (name, value) => {
    setLogisticsFilters((prev) => ({ ...prev, [name]: value }));
  };

  const toggleLogisticsStatus = (statusCode) => {
    setExpandedLogisticsStatuses((prev) => ({
      ...prev,
      [statusCode]: !prev[statusCode],
    }));
  };

  const downloadWb = async (metric) => {
    setWbExporting(metric);
    setError('');
    try {
      const res = await api.downloadAdminAnalyticsWBApiExcel({ ...wbFilters, metric });
      downloadBlob(res, `wb_api_analytics_${metric}.xlsx`);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Не удалось выгрузить Excel.'));
    } finally {
      setWbExporting('');
    }
  };

  const downloadForeign = async (metric) => {
    setForeignExporting(metric);
    setError('');
    try {
      const res = await api.downloadAdminAnalyticsForeignExcel({ ...foreignFilters, metric });
      downloadBlob(res, `foreign_returns_analytics_${metric}.xlsx`);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Не удалось выгрузить Excel.'));
    } finally {
      setForeignExporting('');
    }
  };

  const downloadLogistics = async (metric, statusCode = '') => {
    const exportKey = statusCode ? `status:${statusCode}` : metric;
    setLogisticsExporting(exportKey);
    setError('');
    try {
      const params = { ...logisticsFilters, metric };
      if (statusCode) params.status_code = statusCode;
      const res = await api.downloadAdminAnalyticsLogisticsExcel(params);
      downloadBlob(res, `logistics_analytics_${exportKey.replace(':', '_')}.xlsx`);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Не удалось выгрузить Excel.'));
    } finally {
      setLogisticsExporting('');
    }
  };

  const avgHours = wbData?.summary?.avg_accept_minus_completed_hours;
  const avgCount = wbData?.summary?.avg_count || 0;
  const logisticsSummary = logisticsData?.summary || {};

  return (
    <div className="admin-analytics-page">
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div>
          <h4 className="mb-1 fw-bold">Аналитика</h4>
          <p className="text-muted small mb-0">
            Сводка по возвратам, WB-данным и логистическим статусам.
          </p>
        </div>
        <div className="btn-group">
          <Button
            variant={activeSection === 'wb' ? 'primary' : 'outline-primary'}
            size="sm"
            onClick={() => setActiveSection('wb')}
          >
            Аналитика с данными WB API
          </Button>
          <Button
            variant={activeSection === 'foreign' ? 'primary' : 'outline-primary'}
            size="sm"
            onClick={() => setActiveSection('foreign')}
          >
            Аналитика возвратов по зарубежным заказам
          </Button>
          <Button
            variant={activeSection === 'logistics' ? 'primary' : 'outline-primary'}
            size="sm"
            onClick={() => setActiveSection('logistics')}
          >
            Логистика
          </Button>
        </div>
      </div>

      {error ? (
        <Alert variant="danger" className="py-2 small">
          {error}
        </Alert>
      ) : null}

      {activeSection === 'wb' ? (
        <>
          <Card className="border-0 shadow-sm mb-3 admin-analytics-card">
            <Card.Body>
              <div className="fw-semibold mb-3">Фильтрация</div>
              <Row className="g-3">
                <Col md={6} xl={3}>
                  <Form.Label className="small text-muted">Тип возврата</Form.Label>
                  <Form.Select
                    size="sm"
                    value={wbFilters.return_scope}
                    onChange={(e) => updateWbFilter('return_scope', e.target.value)}
                  >
                    <option value="">Все возвраты</option>
                    <option value="true">Возвраты на ПВЗ</option>
                    <option value="false">Возвраты по зарубежным заказам</option>
                  </Form.Select>
                </Col>
                <Col md={6} xl={3}>
                  <Form.Label className="small text-muted">completedDt от</Form.Label>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={wbFilters.completed_from}
                    onChange={(e) => updateWbFilter('completed_from', e.target.value)}
                  />
                </Col>
                <Col md={6} xl={3}>
                  <Form.Label className="small text-muted">completedDt до</Form.Label>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={wbFilters.completed_to}
                    onChange={(e) => updateWbFilter('completed_to', e.target.value)}
                  />
                </Col>
                <Col md={6} xl={3}>
                  <Form.Label className="small text-muted">destinationOfficeAddress</Form.Label>
                  <Form.Select
                    size="sm"
                    value={wbFilters.our_office}
                    onChange={(e) => updateWbFilter('our_office', e.target.value)}
                  >
                    <option value="">Все ПВЗ</option>
                    <option value="true">Наши ПВЗ</option>
                    <option value="false">Остальные ПВЗ</option>
                  </Form.Select>
                </Col>
                <Col md={9}>
                  <Form.Label className="small text-muted">Магазин</Form.Label>
                  <Form.Control
                    type="search"
                    size="sm"
                    value={wbFilters.store_q}
                    onChange={(e) => updateWbFilter('store_q', e.target.value)}
                    placeholder="Имя магазина или store_id"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') loadWb();
                    }}
                  />
                </Col>
                <Col md={3} className="d-flex align-items-end gap-2">
                  <Button size="sm" onClick={loadWb} disabled={wbLoading}>
                    {wbLoading ? <Spinner animation="border" size="sm" className="me-2" /> : null}
                    Применить
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={wbLoading}
                    onClick={() =>
                      setWbFilters({
                        return_scope: '',
                        completed_from: '',
                        completed_to: '',
                        our_office: '',
                        store_q: '',
                      })
                    }
                  >
                    Сбросить
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Row>
            {WB_CARDS.map((card) => (
              <MetricCard
                key={card.id}
                card={card}
                value={wbData?.cards?.[card.id]}
                loading={wbLoading}
                exporting={wbExporting === card.id}
                onDownload={downloadWb}
              />
            ))}
          </Row>

          <Card className="border-0 shadow-sm admin-analytics-card">
            <Card.Body>
              <div className="fw-semibold mb-1">Сводка</div>
              <div className="small text-muted">
                Среднее время между приемкой товара и completedDt:
              </div>
              <div className="fs-4 fw-bold mt-2">
                {avgHours == null ? '—' : `${avgHours} ч`}
              </div>
              <div className="small text-muted">Расчетов в выборке: {avgCount}</div>
            </Card.Body>
          </Card>
        </>
      ) : activeSection === 'foreign' ? (
        <>
          <Card className="border-0 shadow-sm mb-3 admin-analytics-card">
            <Card.Body>
              <div className="fw-semibold mb-3">Фильтрация</div>
              <Row className="g-3">
                <Col md={4}>
                  <Form.Label className="small text-muted">created_at от</Form.Label>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={foreignFilters.date_from}
                    onChange={(e) => updateForeignFilter('date_from', e.target.value)}
                  />
                </Col>
                <Col md={4}>
                  <Form.Label className="small text-muted">created_at до</Form.Label>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={foreignFilters.date_to}
                    onChange={(e) => updateForeignFilter('date_to', e.target.value)}
                  />
                </Col>
                <Col md={4} className="d-flex align-items-end gap-2">
                  <Button size="sm" onClick={loadForeign} disabled={foreignLoading}>
                    {foreignLoading ? <Spinner animation="border" size="sm" className="me-2" /> : null}
                    Применить
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={foreignLoading}
                    onClick={() => setForeignFilters({ date_from: '', date_to: '' })}
                  >
                    Сбросить
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Row>
            {FOREIGN_CARDS.map((card) => (
              <MetricCard
                key={card.id}
                card={card}
                value={foreignData?.cards?.[card.id]}
                loading={foreignLoading}
                exporting={foreignExporting === card.id}
                onDownload={downloadForeign}
              />
            ))}
          </Row>
        </>
      ) : (
        <>
          <Card className="border-0 shadow-sm mb-3 admin-analytics-card">
            <Card.Body>
              <div className="fw-semibold mb-3">Фильтрация</div>
              <Row className="g-3">
                <Col md={4}>
                  <Form.Label className="small text-muted">readyToReturnDt от</Form.Label>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={logisticsFilters.date_from}
                    onChange={(e) => updateLogisticsFilter('date_from', e.target.value)}
                  />
                </Col>
                <Col md={4}>
                  <Form.Label className="small text-muted">readyToReturnDt до</Form.Label>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={logisticsFilters.date_to}
                    onChange={(e) => updateLogisticsFilter('date_to', e.target.value)}
                  />
                </Col>
                <Col md={4} className="d-flex align-items-end gap-2">
                  <Button size="sm" onClick={loadLogistics} disabled={logisticsLoading}>
                    {logisticsLoading ? <Spinner animation="border" size="sm" className="me-2" /> : null}
                    Применить
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={logisticsLoading}
                    onClick={() => setLogisticsFilters({ date_from: '', date_to: '' })}
                  >
                    Сбросить
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Row>
            <Col md={4} className="mb-3">
              <Card className="border-0 shadow-sm h-100 admin-analytics-card">
                <Card.Body>
                  <div className="small text-muted">Заехало всего</div>
                  <div className="fs-3 fw-bold">
                    {logisticsLoading ? <Spinner animation="border" size="sm" /> : logisticsSummary.arrived_total || 0}
                  </div>
                  <div className="small text-muted">Orders с заполненным readyToReturnDt.</div>
                  <Button
                    variant="outline-success"
                    size="sm"
                    className="mt-2"
                    disabled={logisticsLoading || Boolean(logisticsExporting)}
                    onClick={() => downloadLogistics('all')}
                  >
                    {logisticsExporting === 'all' ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Экспорт...
                      </>
                    ) : (
                      'Download Excel'
                    )}
                  </Button>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4} className="mb-3">
              <Card className="border-0 shadow-sm h-100 admin-analytics-card">
                <Card.Body>
                  <div className="small text-muted">Перевели в другой статус</div>
                  <div className="fs-3 fw-bold">
                    {logisticsLoading ? <Spinner animation="border" size="sm" /> : logisticsSummary.moved_total || 0}
                  </div>
                  <div className="small text-muted">Все кроме «Ожидает получения в WB».</div>
                  <Button
                    variant="outline-success"
                    size="sm"
                    className="mt-2"
                    disabled={logisticsLoading || Boolean(logisticsExporting)}
                    onClick={() => downloadLogistics('moved')}
                  >
                    {logisticsExporting === 'moved' ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Экспорт...
                      </>
                    ) : (
                      'Download Excel'
                    )}
                  </Button>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4} className="mb-3">
              <Card className="border-0 shadow-sm h-100 admin-analytics-card">
                <Card.Body>
                  <div className="small text-muted">Остались в начальном статусе</div>
                  <div className="fs-3 fw-bold">
                    {logisticsLoading ? <Spinner animation="border" size="sm" /> : logisticsSummary.initial_total || 0}
                  </div>
                  <div className="small text-muted">Еще не переведены дальше по процессу.</div>
                  <Button
                    variant="outline-success"
                    size="sm"
                    className="mt-2"
                    disabled={logisticsLoading || Boolean(logisticsExporting)}
                    onClick={() => downloadLogistics('initial')}
                  >
                    {logisticsExporting === 'initial' ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Экспорт...
                      </>
                    ) : (
                      'Download Excel'
                    )}
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="fw-semibold">Текущие статусы заехавших возвратов</div>
            {logisticsLoading ? <Spinner animation="border" size="sm" /> : null}
          </div>

          {(logisticsData?.status_groups || []).length ? (
            (logisticsData.status_groups || []).map((group) => (
              <LogisticsStatusGroup
                key={group.status}
                group={group}
                expanded={Boolean(expandedLogisticsStatuses[group.status])}
                exporting={logisticsExporting === `status:${group.status}`}
                onToggle={() => toggleLogisticsStatus(group.status)}
                onDownload={(statusCode) => downloadLogistics('status', statusCode)}
              />
            ))
          ) : logisticsLoading ? null : (
            <Alert variant="secondary" className="py-2 small">
              По выбранному периоду возвраты не найдены.
            </Alert>
          )}
        </>
      )}
    </div>
  );
}

export default AdminConsolePage;
